import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { Bell, Mail, Save, Settings, ChevronDown, Check, Key, Send, ExternalLink, Info, AlertCircle, CheckCircle, MoreVertical, Copy, Trash2, Clock, Users, Plus, X, Loader2, BarChart2, ArrowRight, FlipHorizontal, Forward } from 'lucide-react';

const HEADERS = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const NotificationsManager = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [users, setUsers] = useState([]);
  
  // Resend email configuration for notifications
  const [emailConfig, setEmailConfig] = useState({
    resend_api_key: '',
    resend_api_key_masked: '',
    resend_api_key_set: false,
    resend_from_email: '',
    resend_from_name: 'System Notifications',
    enabled: false
  });
  
  const [newApiKey, setNewApiKey] = useState('');
  const [testEmail, setTestEmail] = useState('');
  
  // Notifications list
  const [notifications, setNotifications] = useState([]);
  
  // For dropdown states
  const [recipientDropdownOpen, setRecipientDropdownOpen] = useState(null);
  const [typeDropdownOpen, setTypeDropdownOpen] = useState(null);
  const [menuOpen, setMenuOpen] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('[data-dropdown]')) {
        setRecipientDropdownOpen(null);
        setTypeDropdownOpen(null);
        setMenuOpen(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Fetch notification settings
      const settingsRes = await axios.get(`${API}/admin/notification-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (settingsRes.data) {
        setEmailConfig(settingsRes.data.email_config || {
          resend_api_key: '',
          resend_api_key_masked: '',
          resend_api_key_set: false,
          resend_from_email: '',
          resend_from_name: 'System Notifications',
          enabled: false
        });
        
        const existingNotifications = settingsRes.data.notifications || [];
        setNotifications(existingNotifications);
        
        // Always check for missing default notifications (e.g., newly added ones)
        // This ensures new notification types are added to existing configurations
        await initializeDefaults();
      }
      
      // Fetch users for recipient selection
      const usersRes = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(usersRes.data.filter(u => u.email)); // Only users with email
      
    } catch (error) {
      console.error('Error fetching notification settings:', error);
      // Initialize with defaults if no settings exist
      setEmailConfig({
        resend_api_key: '',
        resend_api_key_masked: '',
        resend_api_key_set: false,
        resend_from_email: '',
        resend_from_name: 'System Notifications',
        enabled: false
      });
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  const initializeDefaults = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/admin/notification-settings/initialize-defaults`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.notifications) {
        setNotifications(response.data.notifications);
      }
    } catch (error) {
      console.error('Error initializing default notifications:', error);
    }
  };

  const saveEmailConfig = async () => {
    setSaving(true);
    try {
      const updateData = {
        resend_from_email: emailConfig.resend_from_email,
        resend_from_name: emailConfig.resend_from_name,
        enabled: emailConfig.enabled
      };
      
      // Only include API key if a new one was entered
      if (newApiKey) {
        updateData.resend_api_key = newApiKey;
      }
      
      await axios.put(`${API}/admin/notification-settings/email`, updateData, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      toast.success('Notification email settings saved');
      setNewApiKey('');
      fetchData(); // Refresh to show updated masked key
    } catch (error) {
      console.error('Error saving email config:', error);
      toast.error(error.response?.data?.detail || 'Failed to save email configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    if (!testEmail) {
      toast.error('Please enter a test email address');
      return;
    }
    
    setTesting(true);
    try {
      const response = await axios.post(`${API}/admin/notification-settings/test`, 
        { test_email: testEmail },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      
      toast.success(response.data.message || 'Test notification email sent successfully');
    } catch (error) {
      console.error('Error sending test email:', error);
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally {
      setTesting(false);
    }
  };

  const updateNotificationType = async (notificationId, newType) => {
    try {
      await axios.put(`${API}/admin/notification-settings/notification/${notificationId}/config`, {
        notification_type: newType
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      // Update local state
      setNotifications(prev => prev.map(n => 
        n.id === notificationId ? { ...n, notification_type: newType } : n
      ));
      
      setTypeDropdownOpen(null);
      toast.success('Notification type updated');
    } catch (error) {
      console.error('Error updating notification type:', error);
      toast.error('Failed to update notification type');
    }
  };

  const updateNotificationSendTime = async (notificationId, sendTime) => {
    try {
      await axios.put(`${API}/admin/notification-settings/notification/${notificationId}/config`, {
        send_time: sendTime
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      // Update local state
      setNotifications(prev => prev.map(n => 
        n.id === notificationId ? { ...n, send_time: sendTime } : n
      ));
      
      toast.success('Send time updated');
    } catch (error) {
      console.error('Error updating send time:', error);
      toast.error('Failed to update send time');
    }
  };

  const sendNotificationNow = async (notificationId) => {
    try {
      toast.loading('Sending notification...', { id: 'send-now' });
      
      const response = await axios.post(`${API}/admin/notification-settings/send-now/${notificationId}`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      toast.success(response.data.message || 'Notification sent successfully', { id: 'send-now', duration: 5000 });
      setMenuOpen(null);
    } catch (error) {
      console.error('Error sending notification:', error);
      toast.error(error.response?.data?.detail || 'Failed to send notification', { id: 'send-now' });
    }
  };

  const updateNotificationRecipients = async (notificationId, recipients) => {
    try {
      await axios.put(`${API}/admin/notification-settings/notification/${notificationId}/config`, {
        recipients
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      // Update local state
      setNotifications(prev => prev.map(n => 
        n.id === notificationId ? { ...n, recipients } : n
      ));
      
      toast.success('Recipients updated');
    } catch (error) {
      console.error('Error updating recipients:', error);
      toast.error('Failed to update recipients');
    }
  };

  const [recipientEmailInput, setRecipientEmailInput] = useState({});

  const addManualRecipient = (notificationId) => {
    const email = (recipientEmailInput[notificationId] || '').trim().toLowerCase();
    if (!email) return;
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      toast.error('Enter a valid email address');
      return;
    }
    const notification = notifications.find(n => n.id === notificationId);
    const currentRecipients = notification?.recipients || [];
    if (currentRecipients.includes(email)) {
      toast.error('That email is already a recipient');
      return;
    }
    const newRecipients = [...currentRecipients, email];
    setNotifications(prev => prev.map(n => n.id === notificationId ? { ...n, recipients: newRecipients } : n));
    updateNotificationRecipients(notificationId, newRecipients);
    setRecipientEmailInput(prev => ({ ...prev, [notificationId]: '' }));
  };

  const toggleRecipient = (notificationId, userEmail) => {
    const notification = notifications.find(n => n.id === notificationId);
    if (!notification) return;
    
    const currentRecipients = notification.recipients || [];
    let newRecipients;
    
    if (currentRecipients.includes(userEmail)) {
      newRecipients = currentRecipients.filter(r => r !== userEmail);
    } else {
      newRecipients = [...currentRecipients, userEmail];
    }
    
    // Update local state immediately for better UX
    setNotifications(prev => prev.map(n => 
      n.id === notificationId ? { ...n, recipients: newRecipients } : n
    ));
    
    // Save to backend
    updateNotificationRecipients(notificationId, newRecipients);
  };

  const copyNotification = async (sourceId, newType = null) => {
    try {
      const response = await axios.post(`${API}/admin/notification-settings/notification/copy`, {
        source_id: sourceId,
        notification_type: newType
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (response.data.notification) {
        setNotifications(prev => [...prev, response.data.notification]);
        toast.success('Notification copied - select recipients for the new notification');
      }
      setMenuOpen(null);
    } catch (error) {
      console.error('Error copying notification:', error);
      toast.error('Failed to copy notification');
    }
  };

  const deleteNotification = async (notificationId) => {
    // Don't allow deleting the last notification of a type
    const notification = notifications.find(n => n.id === notificationId);
    const sameTypeCount = notifications.filter(n => n.trigger === notification?.trigger).length;
    
    if (sameTypeCount <= 1) {
      toast.error('Cannot delete the last notification of this type');
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/notification-settings/notification/${notificationId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      toast.success('Notification deleted');
      setMenuOpen(null);
    } catch (error) {
      console.error('Error deleting notification:', error);
      toast.error('Failed to delete notification');
    }
  };

  const getRecipientNames = (recipients) => {
    if (!recipients || recipients.length === 0) return 'No recipients';
    
    const names = recipients.map(email => {
      const user = users.find(u => u.email === email);
      return user ? (user.name || user.username || email) : email;
    });
    
    if (names.length <= 2) return names.join(', ');
    return `${names[0]}, ${names[1]} +${names.length - 2} more`;
  };

  const getTypeLabel = (notification) => {
    const typeOptions = notification.type_options || [];
    const currentType = notification.notification_type || 'all';
    const option = typeOptions.find(o => o.value === currentType);
    return option?.label || currentType;
  };

  const getTypeDescription = (notification) => {
    const typeOptions = notification.type_options || [];
    const currentType = notification.notification_type || 'all';
    const option = typeOptions.find(o => o.value === currentType);
    return option?.description || '';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Email Setup Section - Resend Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-orange-500" />
            Notification Email Setup (Resend)
          </CardTitle>
          <CardDescription>
            Configure Resend to send notification emails to users
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Setup Instructions */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-700 space-y-2">
                <p className="font-medium">Setup Instructions:</p>
                <ol className="list-decimal list-inside space-y-1 ml-1">
                  <li>Create an account at <a href="https://resend.com" target="_blank" rel="noopener noreferrer" className="underline font-medium inline-flex items-center gap-1">resend.com <ExternalLink className="w-3 h-3" /></a></li>
                  <li>Go to <strong>API Keys</strong> in your Resend dashboard and create a new key</li>
                  <li>Copy the API key (starts with <code className="bg-blue-100 px-1 rounded">re_</code>) and paste below</li>
                  <li><strong>Important:</strong> Go to <a href="https://resend.com/domains" target="_blank" rel="noopener noreferrer" className="underline font-medium inline-flex items-center gap-1">Domains <ExternalLink className="w-3 h-3" /></a> and add/verify your domain to send from your own email</li>
                </ol>
              </div>
            </div>
          </div>
          
          {/* API Key */}
          <div className="space-y-2">
            <Label htmlFor="resend-api-key" className="flex items-center gap-2">
              <Key className="h-4 w-4 text-gray-500" />
              Resend API Key
            </Label>
            {emailConfig.resend_api_key_set ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span className="text-sm text-green-700">API Key configured: <code className="bg-green-100 px-1 rounded">{emailConfig.resend_api_key_masked}</code></span>
                </div>
                <Input
                  id="resend-api-key"
                  type="password"
                  placeholder="Enter new API key to update..."
                  value={newApiKey}
                  onChange={(e) => setNewApiKey(e.target.value)}
                  data-testid="notification-resend-api-key"
                />
                <p className="text-xs text-gray-500">Leave blank to keep current key, or enter a new key to replace it</p>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <AlertCircle className="h-4 w-4 text-amber-600" />
                  <span className="text-sm text-amber-700">No API key configured - notifications cannot be sent</span>
                </div>
                <Input
                  id="resend-api-key"
                  type="password"
                  placeholder="re_xxxxxxxxxxxxxxxxxxxxxxxxx"
                  value={newApiKey}
                  onChange={(e) => setNewApiKey(e.target.value)}
                  data-testid="notification-resend-api-key"
                />
              </div>
            )}
          </div>
          
          {/* From Email & Name */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="from-email">From Email Address</Label>
              <Input
                id="from-email"
                type="email"
                placeholder="notifications@yourdomain.com"
                value={emailConfig.resend_from_email}
                onChange={(e) => setEmailConfig(prev => ({ ...prev, resend_from_email: e.target.value }))}
                data-testid="notification-from-email"
              />
              <p className="text-xs text-gray-500">Must be from a verified domain in Resend</p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="from-name">From Name</Label>
              <Input
                id="from-name"
                placeholder="System Notifications"
                value={emailConfig.resend_from_name}
                onChange={(e) => setEmailConfig(prev => ({ ...prev, resend_from_name: e.target.value }))}
                data-testid="notification-from-name"
              />
              <p className="text-xs text-gray-500">Display name shown in recipient's inbox</p>
            </div>
          </div>
          
          {/* Test Email */}
          <div className="space-y-2">
            <Label htmlFor="test-email">Test Notification Email</Label>
            <div className="flex gap-2">
              <Input
                id="test-email"
                type="email"
                placeholder="test@example.com"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                className="flex-1"
                data-testid="notification-test-email"
              />
              <Button 
                onClick={handleTestEmail}
                disabled={testing || !emailConfig.resend_api_key_set}
                variant="outline"
                data-testid="send-test-notification-btn"
              >
                {testing ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600" />
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Send Test
                  </>
                )}
              </Button>
            </div>
            <p className="text-xs text-gray-500">Send a test notification email to verify your configuration</p>
          </div>
          
          {/* Enable Toggle & Save */}
          <div className="flex items-center justify-between pt-4 border-t">
            <div className="flex items-center gap-3">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={emailConfig.enabled}
                  onChange={(e) => setEmailConfig(prev => ({ ...prev, enabled: e.target.checked }))}
                  disabled={!emailConfig.resend_api_key_set && !newApiKey}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
              </label>
              <Label className="cursor-pointer">Enable notification emails</Label>
              {!emailConfig.resend_api_key_set && !newApiKey && (
                <span className="text-xs text-amber-600">(Configure API key first)</span>
              )}
            </div>
            
            <Button 
              onClick={saveEmailConfig} 
              disabled={saving}
              className="bg-orange-500 hover:bg-orange-600"
              data-testid="save-notification-config-btn"
            >
              {saving ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save Email Setup
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Notifications List Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-orange-500" />
            System Notifications
          </CardTitle>
          <CardDescription>
            Configure which users receive each type of notification. Use the 3-dot menu to create copies with different settings.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {notifications.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Bell className="h-12 w-12 mx-auto mb-4 opacity-30" />
              <p className="text-lg font-medium">No notifications configured</p>
              <p className="text-sm mt-1">
                Notifications will appear here as they are set up in the system
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Table Header */}
              <div className="grid grid-cols-12 gap-4 px-4 py-2 bg-gray-50 rounded-lg text-sm font-medium text-gray-600">
                <div className="col-span-3">Notification Name</div>
                <div className="col-span-3">Type</div>
                <div className="col-span-5">Recipients</div>
                <div className="col-span-1"></div>
              </div>
              
              {/* Notification Rows */}
              {notifications.map((notification) => (
                <div 
                  key={notification.id}
                  className="bg-white border rounded-lg hover:bg-gray-50 transition-colors"
                  data-testid={`notification-row-${notification.id}`}
                >
                  <div className="grid grid-cols-12 gap-4 px-4 py-3 items-center">
                    {/* Name */}
                    <div className="col-span-3">
                      <p className="font-medium text-gray-900">{notification.name}</p>
                    </div>
                    
                    {/* Type Dropdown - or Time Picker for goods_in_pending */}
                    <div className="col-span-3 relative" data-dropdown>
                      {(notification.trigger === 'goods_in_pending_c30' || notification.trigger === 'goods_in_pending_e70') ? (
                        <div className="flex items-center gap-2 px-2 py-1.5">
                          <Clock className="h-4 w-4 text-gray-400 flex-shrink-0" />
                          <input
                            type="time"
                            value={notification.send_time || '16:00'}
                            onChange={(e) => updateNotificationSendTime(notification.id, e.target.value)}
                            className="w-full px-2 py-1.5 text-sm border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white"
                            data-testid={`send-time-${notification.id}`}
                          />
                        </div>
                      ) : notification.type_options && notification.type_options.length > 0 ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setTypeDropdownOpen(typeDropdownOpen === notification.id ? null : notification.id);
                            setRecipientDropdownOpen(null);
                            setMenuOpen(null);
                          }}
                          className="w-full flex items-center justify-between px-3 py-2 text-sm border rounded-lg hover:bg-gray-100 transition-colors text-left"
                          data-testid={`type-dropdown-${notification.id}`}
                        >
                          <span className="truncate text-gray-700 font-medium">
                            {getTypeLabel(notification)}
                          </span>
                          <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${
                            typeDropdownOpen === notification.id ? 'rotate-180' : ''
                          }`} />
                        </button>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          <Mail className="h-3 w-3 mr-1" />
                          Email
                        </span>
                      )}
                      
                      {/* Type Dropdown Menu */}
                      {typeDropdownOpen === notification.id && notification.type_options && (
                        <div className="absolute z-50 mt-1 w-72 bg-white border rounded-lg shadow-lg">
                          <div className="p-2 border-b bg-gray-50">
                            <p className="text-xs font-medium text-gray-600">Select notification trigger type</p>
                          </div>
                          <div className="p-1">
                            {notification.type_options.map((option) => (
                              <button
                                key={option.value}
                                onClick={() => updateNotificationType(notification.id, option.value)}
                                className={`w-full text-left px-3 py-3 text-sm rounded hover:bg-gray-50 transition-colors ${
                                  notification.notification_type === option.value ? 'bg-orange-50' : ''
                                }`}
                                data-testid={`type-option-${option.value}`}
                              >
                                <div className="flex items-center gap-2">
                                  <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${
                                    notification.notification_type === option.value ? 'bg-orange-500 border-orange-500' : 'border-gray-300'
                                  }`}>
                                    {notification.notification_type === option.value && <Check className="h-3 w-3 text-white" />}
                                  </div>
                                  <span className="font-medium text-gray-900">{option.label}</span>
                                </div>
                                <p className="text-xs text-gray-500 mt-1 ml-6">{option.description}</p>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Time Selector - For error_log_report and stock_reminder (not goods_in_pending, handled above) */}
                    {((notification.trigger === 'error_log_report' && notification.notification_type !== 'all') ||
                      (notification.trigger === 'stock_reminder' && notification.notification_type !== 'all')) && (
                      <div className="col-span-2 relative">
                        <div className="flex items-center gap-1">
                          <Clock className="h-4 w-4 text-gray-400" />
                          <input
                            type="time"
                            value={notification.send_time || '16:00'}
                            onChange={(e) => updateNotificationSendTime(notification.id, e.target.value)}
                            className="w-full px-2 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white"
                            data-testid={`send-time-${notification.id}`}
                          />
                        </div>
                      </div>
                    )}
                    
                    {/* Recipients Dropdown */}
                    <div className={`relative ${((notification.trigger === 'error_log_report' && notification.notification_type !== 'all') || (notification.trigger === 'stock_reminder' && notification.notification_type !== 'all')) ? 'col-span-3' : 'col-span-5'}`} data-dropdown>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setRecipientDropdownOpen(recipientDropdownOpen === notification.id ? null : notification.id);
                          setTypeDropdownOpen(null);
                          setMenuOpen(null);
                        }}
                        className="w-full flex items-center justify-between px-3 py-2 text-sm border rounded-lg hover:bg-gray-100 transition-colors text-left"
                        data-testid={`recipients-dropdown-${notification.id}`}
                      >
                        <span className="truncate text-gray-700">
                          {getRecipientNames(notification.recipients)}
                        </span>
                        <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${
                          recipientDropdownOpen === notification.id ? 'rotate-180' : ''
                        }`} />
                      </button>
                      
                      {/* Recipients Dropdown Menu */}
                      {recipientDropdownOpen === notification.id && (
                        <div className="absolute z-50 mt-1 w-full bg-white border rounded-lg shadow-lg max-h-64 overflow-y-auto">
                          <div className="p-2 border-b bg-gray-50">
                            <p className="text-xs font-medium text-gray-600">Select users to receive this notification</p>
                          </div>
                          <div className="p-1">
                            {users.map((user) => {
                              const isSelected = (notification.recipients || []).includes(user.email);
                              return (
                                <button
                                  key={user.id}
                                  onClick={() => toggleRecipient(notification.id, user.email)}
                                  className={`w-full flex items-center gap-2 px-3 py-2 text-sm rounded hover:bg-gray-50 transition-colors ${
                                    isSelected ? 'bg-orange-50' : ''
                                  }`}
                                  data-testid={`recipient-option-${user.id}`}
                                >
                                  <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                                    isSelected ? 'bg-orange-500 border-orange-500' : 'border-gray-300'
                                  }`}>
                                    {isSelected && <Check className="h-3 w-3 text-white" />}
                                  </div>
                                  <div className="flex-1 text-left">
                                    <p className="font-medium text-gray-900">
                                      {user.name || user.username || 'Unknown'}
                                    </p>
                                    <p className="text-xs text-gray-500">{user.email}</p>
                                  </div>
                                </button>
                              );
                            })}
                          </div>

                          {/* Manually-typed extra emails (not tied to a system user) */}
                          {(notification.recipients || []).filter(r => !users.some(u => u.email === r)).length > 0 && (
                            <div className="p-2 border-t bg-gray-50/60">
                              <p className="text-[11px] font-medium text-gray-500 mb-1.5">Additional email addresses</p>
                              <div className="flex flex-wrap gap-1.5">
                                {(notification.recipients || []).filter(r => !users.some(u => u.email === r)).map(email => (
                                  <span
                                    key={email}
                                    className="inline-flex items-center gap-1 text-[11px] bg-blue-50 text-blue-700 border border-blue-200 px-2 py-0.5 rounded-full"
                                    data-testid={`extra-recipient-${notification.id}-${email}`}
                                  >
                                    {email}
                                    <button
                                      onClick={() => toggleRecipient(notification.id, email)}
                                      className="hover:text-red-500"
                                      data-testid={`remove-extra-recipient-${notification.id}-${email}`}
                                    >
                                      <X className="h-2.5 w-2.5" />
                                    </button>
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          <div className="p-2 border-t" data-dropdown>
                            <p className="text-[11px] font-medium text-gray-500 mb-1.5">Add an email address manually</p>
                            <div className="flex gap-1.5">
                              <Input
                                value={recipientEmailInput[notification.id] || ''}
                                onChange={e => setRecipientEmailInput(prev => ({ ...prev, [notification.id]: e.target.value }))}
                                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addManualRecipient(notification.id); } }}
                                onClick={e => e.stopPropagation()}
                                placeholder="name@example.com"
                                className="text-xs h-8"
                                data-testid={`manual-recipient-input-${notification.id}`}
                              />
                              <Button
                                size="sm"
                                className="h-8 px-2.5 bg-orange-500 hover:bg-orange-600 flex-shrink-0"
                                onClick={(e) => { e.stopPropagation(); addManualRecipient(notification.id); }}
                                data-testid={`add-manual-recipient-btn-${notification.id}`}
                              >
                                <Plus className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </div>
                          <div className="p-2 border-t">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="w-full text-gray-600"
                              onClick={() => setRecipientDropdownOpen(null)}
                            >
                              Done
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* 3-dot Menu */}
                    <div className="col-span-1 relative flex justify-end" data-dropdown>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setMenuOpen(menuOpen === notification.id ? null : notification.id);
                          setRecipientDropdownOpen(null);
                          setTypeDropdownOpen(null);
                        }}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        data-testid={`notification-menu-${notification.id}`}
                      >
                        <MoreVertical className="h-4 w-4 text-gray-500" />
                      </button>
                      
                      {/* Menu Dropdown */}
                      {menuOpen === notification.id && (
                        <div className="absolute right-0 z-50 mt-8 w-48 bg-white border rounded-lg shadow-lg">
                          <div className="py-1">
                            <button
                              onClick={() => sendNotificationNow(notification.id)}
                              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-green-700 hover:bg-green-50 transition-colors"
                              data-testid={`send-now-${notification.id}`}
                            >
                              <Send className="h-4 w-4" />
                              Send Now
                            </button>
                            <button
                              onClick={() => copyNotification(notification.id)}
                              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                              data-testid={`copy-notification-${notification.id}`}
                            >
                              <Copy className="h-4 w-4" />
                              Copy Notification
                            </button>
                            {notifications.filter(n => n.trigger === notification.trigger).length > 1 && (
                              <button
                                onClick={() => deleteNotification(notification.id)}
                                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                                data-testid={`delete-notification-${notification.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                                Delete
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Type Description Row */}
                  {notification.type_options && notification.type_options.length > 0 && (
                    <div className="px-4 pb-3 pt-0">
                      <p className="text-xs text-gray-500 bg-gray-50 rounded px-3 py-2">
                        {getTypeDescription(notification)}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Team Performance Digest ─────────────────────────────────────── */}
      <TeamDigestSection />

      {/* ── Order Forwarding ───────────────────────────────────────────────── */}
      <OrderForwardingSection />

      {/* ── Stock Efficiency Reports ─────────────────────────────────────── */}
      <StockEfficiencyEmailSection />

      {/* Info Card */}
      <Card className="bg-gray-50 border-gray-200">
        <CardContent className="pt-6">
          <div className="flex gap-3">
            <Settings className="h-5 w-5 text-gray-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-gray-700">How Notifications Work</p>
              <p className="text-sm text-gray-600 mt-1">
                When a notification is triggered, the system will send an email to all selected recipients 
                using their registered email address. Use the <strong>Copy</strong> feature to create multiple 
                notification rules with different types for different users (e.g., "Variance" alerts to managers, 
                "All" notifications to admins).
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════════════════
   Team Performance Digest — standalone sub-section
══════════════════════════════════════════════════════════════════════════ */
function TeamDigestSection() {
  const [configs, setConfigs]     = useState([]);
  const [teams, setTeams]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(null); // config id being saved
  const [sending, setSending]     = useState(null); // config id being sent

  // Load configs + teams in parallel
  useEffect(() => {
    (async () => {
      try {
        const [cfgRes, teamRes] = await Promise.all([
          axios.get(`${API}/admin/team-digest-configs`, { headers: HEADERS() }),
          axios.get(`${API}/teams`, { headers: HEADERS() }),
        ]);
        setConfigs(cfgRes.data.configs || []);
        setTeams(teamRes.data.teams || teamRes.data || []);
      } catch (e) {
        toast.error('Failed to load team digest settings');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const updateLocal = (id, patch) =>
    setConfigs(prev => prev.map(c => c.id === id ? { ...c, ...patch } : c));

  const handleCreate = async () => {
    try {
      const res = await axios.post(`${API}/admin/team-digest-configs`,
        { name: 'Team Performance Digest', team_ids: [], send_time: '08:00', is_active: true },
        { headers: HEADERS() }
      );
      setConfigs(prev => [...prev, res.data.config]);
      toast.success('New digest notification created');
    } catch { toast.error('Failed to create'); }
  };

  const handleSave = async (config) => {
    setSaving(config.id);
    try {
      const res = await axios.put(`${API}/admin/team-digest-configs/${config.id}`, config, { headers: HEADERS() });
      updateLocal(config.id, res.data.config);
      toast.success('Saved');
    } catch { toast.error('Failed to save'); } finally { setSaving(null); }
  };

  const handleCopy = async (config) => {
    try {
      const res = await axios.post(`${API}/admin/team-digest-configs/${config.id}/copy`, {}, { headers: HEADERS() });
      setConfigs(prev => [...prev, res.data.config]);
      toast.success('Copy created');
    } catch { toast.error('Failed to copy'); }
  };

  const handleDelete = async (configId) => {
    if (!window.confirm('Delete this digest notification?')) return;
    try {
      await axios.delete(`${API}/admin/team-digest-configs/${configId}`, { headers: HEADERS() });
      setConfigs(prev => prev.filter(c => c.id !== configId));
      toast.success('Deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const handleSendNow = async (config) => {
    setSending(config.id);
    try {
      const res = await axios.post(`${API}/admin/team-digest-configs/${config.id}/send-now`, {}, { headers: HEADERS() });
      toast.success(res.data.message || 'Digest sent');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send');
    } finally { setSending(null); }
  };

  const toggleTeam = (config, teamId) => {
    const existing = config.team_ids || [];
    const next = existing.includes(teamId)
      ? existing.filter(t => t !== teamId)
      : [...existing, teamId];
    updateLocal(config.id, { team_ids: next });
  };

  return (
    <Card className="border-purple-200">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
              <BarChart2 className="w-4 h-4 text-purple-600" />
            </div>
            <div>
              <CardTitle className="text-base">Team Performance Digest</CardTitle>
              <CardDescription className="text-xs mt-0.5">
                Send each team member a personalised daily performance overview at a set time
              </CardDescription>
            </div>
          </div>
          <Button size="sm" onClick={handleCreate} className="bg-purple-600 hover:bg-purple-700 text-white gap-1">
            <Plus className="w-3 h-3" /> New Digest
          </Button>
        </div>
      </CardHeader>

      <CardContent>
        {loading && (
          <div className="flex justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin text-purple-500" />
          </div>
        )}

        {!loading && configs.length === 0 && (
          <div className="text-center py-8 text-gray-400">
            <BarChart2 className="w-8 h-8 mx-auto mb-2 opacity-30" />
            <p className="text-sm">No team digest notifications yet. Click <strong>New Digest</strong> to create one.</p>
          </div>
        )}

        <div className="space-y-4">
          {configs.map(config => (
            <DigestConfigCard
              key={config.id}
              config={config}
              teams={teams}
              isSaving={saving === config.id}
              isSending={sending === config.id}
              onChange={patch => updateLocal(config.id, patch)}
              onToggleTeam={teamId => toggleTeam(config, teamId)}
              onSave={() => handleSave(config)}
              onCopy={() => handleCopy(config)}
              onDelete={() => handleDelete(config.id)}
              onSendNow={() => handleSendNow(config)}
            />
          ))}
        </div>

        {/* What members receive note */}
        {configs.length > 0 && (
          <div className="mt-4 p-3 bg-purple-50 rounded-xl border border-purple-100 flex gap-2">
            <Info className="w-4 h-4 text-purple-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-purple-700 leading-relaxed">
              Each team member receives only their own performance stats — Quotes, Orders, CRM Uploads, Calls Made, and CRM Tasks (today&apos;s tasks, overdue &amp; upcoming quotes). A link to the C30 dashboard is included.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}


function DigestConfigCard({ config, teams, isSaving, isSending, onChange, onToggleTeam, onSave, onCopy, onDelete, onSendNow }) {
  const [teamPickerOpen, setTeamPickerOpen] = useState(false);
  const pickerRef = useRef(null);

  // Close team picker on outside click
  useEffect(() => {
    const handler = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setTeamPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selectedTeams = teams.filter(t => (config.team_ids || []).includes(t.id));
  const teamDisplayName = (t) => t.team_name || t.name || 'Unknown';

  return (
    <div className="border border-gray-200 rounded-xl p-4 bg-white hover:border-purple-200 transition-colors">
      <div className="flex flex-col gap-3">
        {/* Row 1: Name + active toggle + actions */}
        <div className="flex items-center gap-3">
          <Input
            value={config.name || ''}
            onChange={e => onChange({ name: e.target.value })}
            placeholder="Digest name"
            className="flex-1 text-sm font-medium h-8"
            data-testid={`digest-name-${config.id}`}
          />
          {/* Active toggle */}
          <button
            onClick={() => onChange({ is_active: !config.is_active })}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold transition-colors flex-shrink-0 ${
              config.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
            }`}
            data-testid={`digest-active-${config.id}`}
          >
            <div className={`w-2 h-2 rounded-full ${config.is_active ? 'bg-green-500' : 'bg-gray-400'}`} />
            {config.is_active ? 'Active' : 'Paused'}
          </button>
          {/* Actions */}
          <div className="flex gap-1 flex-shrink-0">
            <Button size="sm" variant="ghost" onClick={onCopy} title="Create copy" className="h-7 w-7 p-0 hover:bg-purple-50">
              <Copy className="w-3 h-3" />
            </Button>
            <Button size="sm" variant="ghost" onClick={onDelete} title="Delete" className="h-7 w-7 p-0 hover:bg-red-50 text-red-500">
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
        </div>

        {/* Row 2: Teams picker + send time */}
        <div className="flex items-start gap-3 flex-wrap">
          {/* Teams multi-select */}
          <div className="flex-1 min-w-[200px]" ref={pickerRef}>
            <Label className="text-xs text-gray-500 mb-1 block">Teams</Label>
            <button
              onClick={() => setTeamPickerOpen(v => !v)}
              className="w-full flex items-center justify-between border border-gray-200 rounded-lg px-3 py-1.5 text-sm text-gray-700 hover:border-purple-300 transition-colors bg-white"
              data-testid={`digest-teams-btn-${config.id}`}
            >
              <span className="truncate">
                {selectedTeams.length === 0
                  ? 'Select teams…'
                  : selectedTeams.map(t => teamDisplayName(t)).join(', ')}
              </span>
              <ChevronDown className="w-3 h-3 ml-2 flex-shrink-0 text-gray-400" />
            </button>

            {teamPickerOpen && (
              <div className="absolute z-50 mt-1 min-w-[200px] max-w-[320px] bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
                {teams.length === 0 && (
                  <p className="text-xs text-gray-400 px-3 py-2">No teams configured</p>
                )}
                {teams.map(team => {
                  const checked = (config.team_ids || []).includes(team.id);
                  return (
                    <button
                      key={team.id}
                      onClick={() => onToggleTeam(team.id)}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-purple-50 text-left transition-colors"
                      data-testid={`digest-team-option-${team.id}`}
                    >
                      <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${checked ? 'bg-purple-600 border-purple-600' : 'border-gray-300'}`}>
                        {checked && <Check className="w-2.5 h-2.5 text-white" />}
                      </div>
                      <span className="font-medium text-gray-800">{teamDisplayName(team)}</span>
                      <span className="text-gray-400 text-xs ml-auto">{(team.member_user_ids || []).length} members</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Send time */}
          <div className="flex flex-col">
            <Label className="text-xs text-gray-500 mb-1">Send Time (UTC)</Label>
            <div className="flex items-center gap-1.5 border border-gray-200 rounded-lg px-2.5 py-1.5 bg-white">
              <Clock className="w-3 h-3 text-gray-400 flex-shrink-0" />
              <input
                type="time"
                value={config.send_time || '08:00'}
                onChange={e => onChange({ send_time: e.target.value })}
                className="text-sm font-mono border-0 outline-none bg-transparent"
                data-testid={`digest-time-${config.id}`}
              />
            </div>
          </div>
        </div>

        {/* Selected teams badges */}
        {selectedTeams.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {selectedTeams.map(t => (
              <span key={t.id} className="flex items-center gap-1 text-[11px] bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-medium">
                <Users className="w-2.5 h-2.5" />
                {teamDisplayName(t)}
                <button onClick={() => onToggleTeam(t.id)} className="ml-0.5 hover:text-red-500"><X className="w-2.5 h-2.5" /></button>
              </span>
            ))}
          </div>
        )}

        {/* Row 3: Save + Send Now buttons */}
        <div className="flex items-center gap-2 pt-1">
          <Button
            size="sm"
            onClick={onSave}
            disabled={isSaving}
            className="gap-1 bg-purple-600 hover:bg-purple-700 text-white h-7 text-xs"
            data-testid={`digest-save-${config.id}`}
          >
            {isSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
            Save
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={onSendNow}
            disabled={isSending}
            className="gap-1 h-7 text-xs border-purple-300 text-purple-700 hover:bg-purple-50"
            data-testid={`digest-send-now-${config.id}`}
          >
            {isSending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
            Send Now
          </Button>
          <span className="text-[10px] text-gray-400 ml-1">
            Sends to {selectedTeams.reduce((sum, t) => sum + (t.member_user_ids || t.members || []).length, 0)} member(s)
          </span>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   Order Forwarding — configure rules to forward parsed orders by email
══════════════════════════════════════════════════════════════════════════ */
function OrderForwardingSection() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [testingId, setTestingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const [form, setForm] = useState({ source_user_email: '', forward_to_email: '', label: '' });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/admin/order-forward-rules`, { headers: HEADERS() });
      setRules(r.data.rules || []);
    } catch { toast.error('Failed to load forwarding rules'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.source_user_email.trim() || !form.forward_to_email.trim()) {
      return toast.error('Both email fields are required');
    }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/admin/order-forward-rules`, form, { headers: HEADERS() });
      setRules(prev => [...prev, r.data.rule]);
      setForm({ source_user_email: '', forward_to_email: '', label: '' });
      setShowForm(false);
      toast.success('Forwarding rule created');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to create rule'); }
    finally { setSaving(false); }
  };

  const toggleActive = async (rule) => {
    setTogglingId(rule.id);
    try {
      const r = await axios.put(`${API}/admin/order-forward-rules/${rule.id}`,
        { is_active: !rule.is_active }, { headers: HEADERS() });
      setRules(prev => prev.map(x => x.id === rule.id ? r.data.rule : x));
    } catch { toast.error('Failed to update rule'); }
    finally { setTogglingId(null); }
  };

  const deleteRule = async (id) => {
    if (!window.confirm('Delete this forwarding rule?')) return;
    setDeletingId(id);
    try {
      await axios.delete(`${API}/admin/order-forward-rules/${id}`, { headers: HEADERS() });
      setRules(prev => prev.filter(r => r.id !== id));
      toast.success('Rule deleted');
    } catch { toast.error('Failed to delete rule'); }
    finally { setDeletingId(null); }
  };

  const testRule = async (rule) => {
    setTestingId(rule.id);
    try {
      const r = await axios.post(`${API}/admin/order-forward-rules/${rule.id}/test`,
        {}, { headers: HEADERS() });
      toast.success(r.data.message || 'Test email sent');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Test failed'); }
    finally { setTestingId(null); }
  };

  const ORANGE = '#ee7203';

  return (
    <Card className="border-orange-200">
      <CardHeader className="pb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${ORANGE}15` }}>
            <Forward className="w-5 h-5" style={{ color: ORANGE }} />
          </div>
          <div className="flex-1">
            <CardTitle className="text-base font-bold">Order Forwarding</CardTitle>
            <CardDescription className="text-sm mt-0.5">
              When an order is received from a specific user, automatically email the full parsed order details to another person.
            </CardDescription>
          </div>
          <button
            onClick={() => setShowForm(v => !v)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-white text-sm font-semibold"
            style={{ background: ORANGE }}
            data-testid="add-forward-rule-btn"
          >
            <Plus className="w-4 h-4" /> New Rule
          </button>
        </div>
      </CardHeader>

      <CardContent>
        {/* New rule form */}
        {showForm && (
          <form onSubmit={handleSave} className="mb-5 p-4 rounded-xl border-2 border-orange-200 bg-orange-50/30 space-y-3">
            <p className="text-xs font-bold text-gray-600 uppercase tracking-wider mb-2">New Forwarding Rule</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="src-email" className="text-xs font-semibold">
                  Watch orders from (email) *
                </Label>
                <Input
                  id="src-email"
                  type="email"
                  placeholder="r.eyre@encon.co.uk"
                  value={form.source_user_email}
                  onChange={e => setForm(p => ({ ...p, source_user_email: e.target.value }))}
                  required
                  data-testid="rule-source-email"
                  className="text-sm"
                />
                <p className="text-[10px] text-gray-400">The sender email the automation watches for</p>
              </div>
              <div className="space-y-1">
                <Label htmlFor="fwd-email" className="text-xs font-semibold">
                  Forward to (email) *
                </Label>
                <Input
                  id="fwd-email"
                  type="email"
                  placeholder="n.maloney@encon.co.uk"
                  value={form.forward_to_email}
                  onChange={e => setForm(p => ({ ...p, forward_to_email: e.target.value }))}
                  required
                  data-testid="rule-forward-email"
                  className="text-sm"
                />
                <p className="text-[10px] text-gray-400">Who receives the forwarded order email</p>
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="rule-label" className="text-xs font-semibold">Label (optional)</Label>
              <Input
                id="rule-label"
                placeholder="e.g. Eyre → Maloney M4 Orders"
                value={form.label}
                onChange={e => setForm(p => ({ ...p, label: e.target.value }))}
                data-testid="rule-label"
                className="text-sm"
              />
            </div>
            <div className="flex gap-2 pt-1">
              <button type="submit" disabled={saving}
                className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-white text-sm font-semibold disabled:opacity-60"
                style={{ background: ORANGE }}
                data-testid="rule-save-btn">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                Create Rule
              </button>
              <button type="button" onClick={() => setShowForm(false)}
                className="px-4 py-2 rounded-xl text-sm font-semibold border border-gray-200 text-gray-600 hover:bg-gray-50">
                Cancel
              </button>
            </div>
          </form>
        )}

        {/* Rules list */}
        {loading ? (
          <div className="flex items-center justify-center py-8 gap-2 text-gray-400">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span className="text-sm">Loading rules…</span>
          </div>
        ) : rules.length === 0 && !showForm ? (
          <div className="text-center py-8">
            <Forward className="w-8 h-8 mx-auto mb-2 text-gray-200" />
            <p className="text-sm text-gray-500 font-medium">No forwarding rules yet</p>
            <p className="text-xs text-gray-400 mt-1">Create a rule to auto-forward parsed orders by email.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {rules.map(rule => (
              <div key={rule.id}
                data-testid={`forward-rule-${rule.id}`}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl border-2 transition-colors ${
                  rule.is_active ? 'border-orange-200 bg-orange-50/20' : 'border-gray-200 bg-gray-50/50 opacity-60'
                }`}>
                {/* Active toggle */}
                <button
                  onClick={() => toggleActive(rule)}
                  disabled={togglingId === rule.id}
                  title={rule.is_active ? 'Click to disable' : 'Click to enable'}
                  className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${
                    rule.is_active ? 'text-white' : 'bg-gray-200 text-gray-400'
                  }`}
                  style={rule.is_active ? { background: ORANGE } : {}}
                  data-testid={`rule-toggle-${rule.id}`}
                >
                  {togglingId === rule.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                </button>

                {/* Rule info */}
                <div className="flex-1 min-w-0">
                  {rule.label && <p className="text-xs font-bold text-gray-700 mb-0.5">{rule.label}</p>}
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-semibold text-gray-800 bg-gray-100 px-2 py-0.5 rounded-full">{rule.source_user_email}</span>
                    <ArrowRight className="w-3 h-3 text-gray-400 flex-shrink-0" />
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: `${ORANGE}20`, color: ORANGE }}>{rule.forward_to_email}</span>
                  </div>
                  <p className="text-[10px] text-gray-400 mt-0.5">
                    Subject: <span className="italic">Order Confirmation - {rule.source_user_email}</span>
                  </p>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <button
                    onClick={() => testRule(rule)}
                    disabled={testingId === rule.id}
                    title="Send test email"
                    className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold border border-gray-200 text-gray-600 hover:border-orange-300 hover:text-orange-600 disabled:opacity-50 transition-colors"
                    data-testid={`rule-test-${rule.id}`}
                  >
                    {testingId === rule.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                    Test
                  </button>
                  <button
                    onClick={() => deleteRule(rule.id)}
                    disabled={deletingId === rule.id}
                    title="Delete rule"
                    className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                    data-testid={`rule-delete-${rule.id}`}
                  >
                    {deletingId === rule.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* How it works hint */}
        <div className="mt-4 p-3 rounded-lg bg-gray-50 border border-gray-200">
          <p className="text-[11px] text-gray-500 leading-relaxed">
            <strong className="text-gray-700">How it works:</strong> When a new order arrives in Automations from the "watch" email, the system
            parses it and automatically sends a branded HTML email to the forwarding address.
            The <strong>Subject</strong> is: <code className="bg-gray-200 px-1 rounded text-[10px]">Order Confirmation - {'{'}watched email{'}'}</code> — so it's always clear which mailbox the order was parsed from.
            The email body shows the full parsed order view with product lines, addresses, and delivery date.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   Stock Efficiency Reports — per-branch email settings
══════════════════════════════════════════════════════════════════════════ */
function StockEfficiencyEmailSection() {
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingCode, setSavingCode] = useState(null);
  const [testingCode, setTestingCode] = useState(null);
  const [emailDraft, setEmailDraft] = useState({}); // branch_code -> draft text for the "add address" input
  const load = async () => {
    try {
      const r = await axios.get(`${API}/stock-ordering/efficiency/email-settings`, { headers: HEADERS() });
      setBranches(r.data.branches || []);
    } catch { toast.error('Failed to load Stock Efficiency email settings'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  const saveField = async (branchCode, patch) => {
    setSavingCode(branchCode);
    try {
      await axios.put(`${API}/stock-ordering/efficiency/email-settings/${branchCode}`, patch, { headers: HEADERS() });
      setBranches(prev => prev.map(b => b.branch_code === branchCode ? { ...b, ...patch } : b));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    } finally {
      setSavingCode(null);
    }
  };
  const toggleEnabled = (branch) => saveField(branch.branch_code, { enabled: !branch.enabled });
  const setTriggerMode = (branch, mode) => { if (mode !== branch.trigger_mode) saveField(branch.branch_code, { trigger_mode: mode }); };
  const addEmail = (branch) => {
    const email = (emailDraft[branch.branch_code] || '').trim().toLowerCase();
    if (!email) return;
    if (!/^\S+@\S+\.\S+$/.test(email)) { toast.error('Enter a valid email address'); return; }
    const current = branch.emails || [];
    if (current.includes(email)) { toast.error('That email is already added'); return; }
    saveField(branch.branch_code, { emails: [...current, email] });
    setEmailDraft(prev => ({ ...prev, [branch.branch_code]: '' }));
  };
  const removeEmail = (branch, email) => {
    saveField(branch.branch_code, { emails: (branch.emails || []).filter(e => e !== email) });
  };
  const testSend = async (branchCode) => {
    setTestingCode(branchCode);
    try {
      const r = await axios.post(`${API}/stock-ordering/efficiency/email-settings/${branchCode}/test`, {}, { headers: HEADERS() });
      toast.success(r.data.detail || 'Test email sent');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to send test'); }
    finally { setTestingCode(null); }
  };
  const ORANGE = '#ee7203';
  return (
    <Card className="border-orange-200">
      <CardHeader className="pb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${ORANGE}15` }}>
            <Mail className="w-5 h-5" style={{ color: ORANGE }} />
          </div>
          <div className="flex-1">
            <CardTitle className="text-base font-bold">Stock Efficiency Reports</CardTitle>
            <CardDescription className="text-sm mt-0.5">
              Each branch gets its own Stock Efficiency report — a rich HTML email with a PDF attached. By default it
              sends the moment that branch's first Free Stock Report of the day is uploaded ("On Upload"); switch a
              branch to "Time" to instead send daily at a fixed time. Off by default. Add as many recipient
              addresses per branch as you like.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8 gap-2 text-gray-400">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span className="text-sm">Loading branches…</span>
          </div>
        ) : branches.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-8">No branches found.</p>
        ) : (
          <div className="space-y-2">
            {branches.map(b => (
              <div key={b.branch_code}
                className={`rounded-xl border-2 p-3 transition-colors ${
                  b.enabled ? 'border-orange-200 bg-orange-50/20' : 'border-gray-200 bg-gray-50/50'
                }`}
                data-testid={`stock-eff-branch-row-${b.branch_code}`}
              >
                <div className="flex items-center gap-3 flex-wrap">
                  <button
                    onClick={() => toggleEnabled(b)}
                    title={b.enabled ? 'Click to disable' : 'Click to enable'}
                    className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${
                      b.enabled ? 'text-white' : 'bg-gray-200 text-gray-400'
                    }`}
                    style={b.enabled ? { background: ORANGE } : {}}
                    data-testid={`stock-eff-toggle-${b.branch_code}`}
                  >
                    <Check className="w-3.5 h-3.5" />
                  </button>
                  <div className="w-36 flex-shrink-0">
                    <p className="text-xs font-bold text-gray-700 truncate">{b.branch_name}</p>
                    <p className="text-[10px] text-gray-400">{b.branch_code}</p>
                  </div>
                  {/* Trigger mode segmented control */}
                  <div className="flex items-center rounded-lg border border-gray-200 bg-white p-0.5 flex-shrink-0" data-testid={`stock-eff-trigger-mode-${b.branch_code}`}>
                    <button
                      onClick={() => setTriggerMode(b, 'on_upload')}
                      className={`px-2.5 py-1 rounded-md text-[11px] font-semibold transition-colors ${
                        b.trigger_mode === 'on_upload' ? 'text-white' : 'text-gray-500 hover:text-gray-700'
                      }`}
                      style={b.trigger_mode === 'on_upload' ? { background: ORANGE } : {}}
                      data-testid={`stock-eff-mode-on-upload-${b.branch_code}`}
                    >
                      On Upload
                    </button>
                    <button
                      onClick={() => setTriggerMode(b, 'time')}
                      className={`px-2.5 py-1 rounded-md text-[11px] font-semibold transition-colors ${
                        b.trigger_mode === 'time' ? 'text-white' : 'text-gray-500 hover:text-gray-700'
                      }`}
                      style={b.trigger_mode === 'time' ? { background: ORANGE } : {}}
                      data-testid={`stock-eff-mode-time-${b.branch_code}`}
                    >
                      Time
                    </button>
                  </div>
                  {b.trigger_mode === 'time' && (
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <Clock className="w-3.5 h-3.5 text-gray-400" />
                      <input
                        type="time"
                        value={b.send_time || '07:00'}
                        onChange={e => saveField(b.branch_code, { send_time: e.target.value })}
                        className="px-2 py-1.5 text-sm border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white w-[110px]"
                        data-testid={`stock-eff-time-${b.branch_code}`}
                      />
                    </div>
                  )}
                  <button
                    onClick={() => testSend(b.branch_code)}
                    disabled={testingCode === b.branch_code || (b.emails || []).length === 0}
                    title="Send this branch's report right now"
                    className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold border border-gray-200 text-gray-600 hover:border-orange-300 hover:text-orange-600 disabled:opacity-50 transition-colors flex-shrink-0"
                    data-testid={`stock-eff-test-${b.branch_code}`}
                  >
                    {testingCode === b.branch_code ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                    Test
                  </button>
                  {savingCode === b.branch_code && (
                    <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400 flex-shrink-0" />
                  )}
                </div>

                {/* Recipients — chip list, same add/remove pattern as System Notifications above */}
                <div className="mt-2.5 pl-11">
                  {(b.emails || []).length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-1.5">
                      {(b.emails || []).map(email => (
                        <span
                          key={email}
                          className="inline-flex items-center gap-1 text-[11px] bg-blue-50 text-blue-700 border border-blue-200 px-2 py-0.5 rounded-full"
                          data-testid={`stock-eff-email-chip-${b.branch_code}-${email}`}
                        >
                          {email}
                          <button
                            onClick={() => removeEmail(b, email)}
                            className="hover:text-red-500"
                            data-testid={`stock-eff-remove-email-${b.branch_code}-${email}`}
                          >
                            <X className="h-2.5 w-2.5" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-1.5 max-w-sm">
                    <Input
                      value={emailDraft[b.branch_code] || ''}
                      onChange={e => setEmailDraft(prev => ({ ...prev, [b.branch_code]: e.target.value }))}
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addEmail(b); } }}
                      placeholder="add another address…"
                      className="text-xs h-8"
                      data-testid={`stock-eff-email-input-${b.branch_code}`}
                    />
                    <Button
                      size="sm"
                      className="h-8 px-2.5 bg-orange-500 hover:bg-orange-600 flex-shrink-0"
                      onClick={() => addEmail(b)}
                      data-testid={`stock-eff-add-email-btn-${b.branch_code}`}
                    >
                      <Plus className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="mt-4 p-3 rounded-lg bg-gray-50 border border-gray-200">
          <p className="text-[11px] text-gray-500 leading-relaxed">
            <strong className="text-gray-700">How it works:</strong> The email body itself looks like the in-app report
            (colour-coded finding badges, donor branches, supplier grouping), with a matching PDF attached — not just a plain list.
            Use <strong>Test</strong> to send a branch's report right now regardless of its toggle, to check formatting before turning it on.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
export default NotificationsManager;
